import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, CalendarCheck, FileText, MessageSquare, MessagesSquare, Mail, Bot, LogOut, Menu, X, ExternalLink } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/bookings", label: "Bookings", icon: CalendarCheck },
  { to: "/admin/posts", label: "Blog Posts", icon: FileText },
  { to: "/admin/comments", label: "Comments", icon: MessageSquare },
  { to: "/admin/chat", label: "Chat Desk", icon: MessagesSquare },
  { to: "/admin/contacts", label: "Contact Inbox", icon: Mail },
  { to: "/admin/faqs", label: "Chatbot FAQs", icon: Bot },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const sidebar = (
    <div className="flex flex-col h-full">
      <Link to="/admin" className="flex items-center gap-3 px-5 py-6 border-b border-white/10">
        <img src="/assets/logo.png" alt="Neat & Proper" className="w-10 h-10 rounded-xl bg-white object-cover" />
        <div>
          <p className="font-display font-extrabold text-white leading-tight">Neat &amp; Proper</p>
          <p className="text-[10px] text-brand-lime uppercase tracking-widest font-bold">Admin</p>
        </div>
      </Link>
      <nav className="flex-1 py-4 space-y-1 px-3" data-testid="admin-nav">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => setOpen(false)}
            data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                isActive ? "bg-brand-lime text-brand-emerald-dark" : "text-white/70 hover:bg-white/10 hover:text-white"
              }`
            }
          >
            <n.icon className="w-4 h-4" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 pb-5 space-y-1 border-t border-white/10 pt-4">
        <Link to="/" className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold text-white/60 hover:bg-white/10 hover:text-white transition-colors">
          <ExternalLink className="w-4 h-4" /> View Website
        </Link>
        <button onClick={doLogout} data-testid="admin-logout-btn" className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold text-red-300 hover:bg-red-500/10 transition-colors">
          <LogOut className="w-4 h-4" /> Sign Out
        </button>
        <p className="px-4 pt-2 text-[10px] text-white/40 truncate">{user?.email}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-brand-canvas flex" data-testid="admin-layout">
      <aside className="hidden lg:block w-64 hero-gradient fixed inset-y-0 left-0 z-40">{sidebar}</aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 hero-gradient animate-pop-in">{sidebar}</aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen">
        <header className="lg:hidden sticky top-0 z-30 bg-brand-emerald text-white flex items-center justify-between px-4 py-3">
          <button onClick={() => setOpen(true)} data-testid="admin-mobile-menu-btn" aria-label="Open menu" className="p-2">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <span className="font-display font-bold">Admin Console</span>
          <span className="w-10" />
        </header>
        <main className="flex-1 p-4 sm:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
